import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Box, Typography, CircularProgress, Button } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import AuthCard from '../components/AuthCard';
import { apiError } from '../i18n/errors';
import api from '../api/client';
import { colors } from '../colors';

export default function VerifyEmail() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading'); // loading, success, error
  const [message, setMessage] = useState('');

  useEffect(() => {
    const token = searchParams.get('token');
    if (!token) {
      setStatus('error');
      setMessage('Token de vérification manquant.');
      return;
    }

    api.get(`/auth/verify-email/?token=${token}`)
      .then(({ data }) => {
        setStatus('success');
        setMessage(data.message || 'Email vérifié avec succès !');
      })
      .catch((err) => {
        setStatus('error');
        setMessage(apiError(err));
      });
  }, [searchParams]);

  return (
    <AuthCard title={status === 'loading' ? 'Vérification...' : status === 'success' ? 'Email vérifié !' : 'Erreur'}>
      <Box sx={{ textAlign: 'center' }}>
        {status === 'loading' && (
          <>
            <CircularProgress sx={{ color: colors.primaryLight, mb: 3 }} size={60} />
            <Typography variant="body1" sx={{ color: colors.textSecondary }}>
              Vérification de votre email en cours...
            </Typography>
          </>
        )}

        {status === 'success' && (
          <>
            <CheckCircleIcon sx={{ color: colors.success, fontSize: 64, mb: 3 }} />
            <Typography variant="h6" sx={{ color: colors.textPrimary, mb: 2, fontWeight: 600 }}>
              Votre email est vérifié !
            </Typography>
            <Typography variant="body2" sx={{ color: colors.textSecondary, mb: 4 }}>
              Vous pouvez maintenant vous connecter et profiter de Publy Music.
            </Typography>
            <Button
              variant="contained"
              fullWidth
              size="large"
              onClick={() => navigate('/login')}
              sx={{
                bgcolor: colors.primaryLight,
                py: 1.5,
                fontSize: '1rem',
                fontWeight: 700,
                '&:hover': { bgcolor: colors.primaryLight },
              }}
            >
              Se connecter
            </Button>
          </>
        )}

        {status === 'error' && (
          <>
            <ErrorIcon sx={{ color: colors.error, fontSize: 64, mb: 3 }} />
            <Typography variant="h6" sx={{ color: colors.textPrimary, mb: 2, fontWeight: 600 }}>
              Erreur de vérification
            </Typography>
            <Typography variant="body2" sx={{ color: colors.errorLight, mb: 3 }}>
              {message}
            </Typography>
            <Typography variant="body2" sx={{ color: colors.textSecondary, mb: 4 }}>
              Le lien est peut-être expiré ou invalide.
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Button
                variant="contained"
                fullWidth
                size="large"
                onClick={() => navigate('/login')}
                sx={{
                  bgcolor: colors.primaryLight,
                  py: 1.5,
                  fontSize: '1rem',
                  fontWeight: 700,
                  '&:hover': { bgcolor: colors.primaryLight },
                }}
              >
                Se connecter
              </Button>
              <Typography textAlign="center" variant="body2" sx={{ color: colors.textSecondary }}>
                <Link to="/" style={{ color: colors.textSecondary, textDecoration: 'none' }}>
                  ← Retour à l'accueil
                </Link>
              </Typography>
            </Box>
          </>
        )}
      </Box>
    </AuthCard>
  );
}
